import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * O necessário para TRAÇAR em cima de uma curva, sem biblioteca.
 *
 * ─── Por que não uma biblioteca de gráficos ────────────────────────────
 *
 * O código chega ao servidor digitado num terminal web (decisão D-03).
 * Uma biblioteca de gráficos custa centenas de kB e resolve o problema
 * errado: as prontas desenham dados: eixo, série, tooltip. O que esta
 * tela precisa é o contrário — desenhar dados é a parte fácil; o difícil
 * é deixar a PESSOA arrastar uma reta sobre eles e devolver, em unidades
 * de ensaio, onde ela parou.
 *
 * ─── A regra que este arquivo respeita ─────────────────────────────────
 *
 * **O sistema não traça.** Ele desenha a curva, obedece à mão e refaz a
 * aritmética da norma a partir do que a mão deixou (`D-27`). Não existe
 * «ajustar automaticamente»: um sistema que ajusta a reta e não diz que
 * ajustou está assinando no lugar de alguém.
 *
 * O que ele PODE fazer, e faz, é enquadrar: o primeiro desenho escolhe
 * sozinho a janela que mostra melhor a curva, e a partir daí o zoom é do
 * usuário — foi o que a Testesolo pediu em 23/08/2026.
 */

// ── Escalas ──────────────────────────────────────────────────────────

export type Escala = 'linear' | 'log' | 'raiz';

/**
 * Leva o valor do ensaio para 0…1 dentro da janela, e de volta.
 *
 * Ida e volta no mesmo objeto de propósito: o gráfico converte para
 * desenhar, e converte de volta a cada pixel que o dedo arrasta. Duas
 * funções escritas em lugares diferentes é como o ponto arrastado passa a
 * cair alguns décimos ao lado de onde foi solto.
 */
export interface Conversor {
  para(v: number): number;
  de(u: number): number;
  /** O eixo transformado (log₁₀, raiz ou o próprio valor), para quem
      precisa fazer conta na escala do eixo — o zoom e o arrasto do plano. */
  f(v: number): number;
  g(t: number): number;
}

function conversor(escala: Escala, lo: number, hi: number): Conversor {
  const f = escala === 'log' ? Math.log10
    : escala === 'raiz' ? Math.sqrt
      : (x: number) => x;
  const g = escala === 'log' ? (x: number) => 10 ** x
    : escala === 'raiz' ? (x: number) => x * x
      : (x: number) => x;
  const a = f(lo);
  const b = f(hi);
  const largura = b - a || 1;
  return {
    para: (v) => (f(v) - a) / largura,
    de: (u) => g(a + u * largura),
    f,
    g,
  };
}

/** Uma janela de visualização: o pedaço do domínio que está à mostra. */
export interface Janela { x0: number; x1: number; y0: number; y1: number }

/** O chão de cada escala, NA ESCALA DO EIXO: log e raiz não existem
    abaixo dele, e uma janela que o ultrapassa vira NaN inteira. */
function chao(escala: Escala, lo: number): number {
  if (escala === 'log') return Math.log10(Math.max(1e-9, lo * 0.001));
  if (escala === 'raiz') return 0;
  return -Infinity;
}

/**
 * Uma janela nova a partir de um PEDAÇO da atual, dito em frações (0…1)
 * do que está à mostra — `u0 = 0,25, u1 = 0,75` é «a metade do meio».
 *
 * É a conta única por trás do zoom pela roda e do arrasto pelo botão do
 * meio (Humberto, 14/09/2026). Feita na escala do eixo (log, raiz ou
 * linear), para que aproximar num eixo log continue sendo uma
 * multiplicação e o arrasto não «acelere» de um lado da década para o
 * outro. Quando o pedaço passa do chão da escala, a janela é empurrada
 * de volta inteira — em vez de cortada, o que mudaria a largura sem
 * ninguém ter pedido.
 */
export function recortarJanela(
  j: Janela, ex: Escala, ey: Escala,
  pedaco: { u0: number; u1: number; v0: number; v1: number },
): Janela {
  const cx = conversor(ex, j.x0, j.x1);
  const cy = conversor(ey, j.y0, j.y1);
  const eixo = (c: Conversor, lo: number, hi: number, a: number, b: number, esc: Escala) => {
    const t0 = c.f(lo);
    const w = c.f(hi) - t0;
    let n0 = t0 + a * w;
    let n1 = t0 + b * w;
    const piso = chao(esc, lo);
    if (n0 < piso) { n1 += piso - n0; n0 = piso; }
    return [c.g(n0), c.g(n1)];
  };
  const [x0, x1] = eixo(cx, j.x0, j.x1, pedaco.u0, pedaco.u1, ex);
  const [y0, y1] = eixo(cy, j.y0, j.y1, pedaco.v0, pedaco.v1, ey);
  return { x0, x1, y0, y1 };
}

// ── Números redondos para as marcas do eixo ──────────────────────────

export function passoRedondo(bruto: number): number {
  const ordem = 10 ** Math.floor(Math.log10(bruto));
  const r = bruto / ordem;
  const escolhido = r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10;
  return escolhido * ordem;
}

/**
 * As marcas de um eixo.
 *
 * No log elas são as décadas (1, 10, 100…) e os múltiplos dentro delas,
 * porque é assim que se lê uma curva de compressibilidade — e porque
 * marca «linear» em eixo log cai toda amontoada de um lado.
 */
function marcas(escala: Escala, lo: number, hi: number, quantas = 6): number[] {
  if (escala === 'log') {
    const saida: number[] = [];
    const de = Math.floor(Math.log10(lo));
    const ate = Math.ceil(Math.log10(hi));
    for (let d = de; d <= ate; d += 1) {
      for (const m of [1, 2, 5]) {
        const v = m * 10 ** d;
        if (v >= lo * 0.999 && v <= hi * 1.001) saida.push(v);
      }
    }
    return saida.length > 1 ? saida : [lo, hi];
  }
  if (escala === 'raiz') {
    // Marcas redondas em √t, rotuladas com o t correspondente: a pessoa
    // lê tempo, o gráfico desenha raiz.
    const r0 = Math.sqrt(lo);
    const r1 = Math.sqrt(hi);
    const passo = passoRedondo((r1 - r0) / quantas);
    const saida: number[] = [];
    for (let v = Math.ceil(r0 / passo) * passo; v <= r1 * 1.0001; v += passo) {
      saida.push(v * v);
    }
    return saida.length ? saida : [lo, hi];
  }
  const passo = passoRedondo((hi - lo) / quantas);
  const saida: number[] = [];
  for (let v = Math.ceil(lo / passo) * passo; v <= hi * 1.0001; v += passo) {
    saida.push(v);
  }
  return saida.length ? saida : [lo, hi];
}

export const br = (v: number, casas: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

/**
 * Quantas casas o rótulo precisa para dois valores distantes de um passo
 * saírem diferentes — e nenhuma a mais. Passo 0,05 → 2 casas (19,35 ·
 * 19,40); 0,25 → 2; 0,5 → 1; 50 → 0. A regra anterior somava uma casa por
 * garantia e o eixo saía «19,900 · 19,850» (Humberto, 14/09/2026: «o
 * número de casas decimais também não é necessário»).
 */
export function casasDoPasso(passo: number): number {
  for (let c = 0; c <= 6; c += 1) {
    const v = passo * 10 ** c;
    if (Math.abs(v - Math.round(v)) < 1e-6) return c;
  }
  return 6;
}

/**
 * O rótulo da marca — com casas decidas pelo PASSO, não pela grandeza.
 *
 * Defeito visto na primeira renderização, 23/08/2026: o eixo de altura
 * ia de 19,35 a 19,63 mm e saiu marcado «19,6 · 19,5 · 19,5 · 19,4» — dois
 * rótulos iguais, porque a regra olhava o tamanho do NÚMERO (dezenas → uma
 * casa) e não a distância entre uma marca e a seguinte. Eixo com dois
 * rótulos iguais não é feio: é ilegível, porque a pessoa não sabe qual
 * linha da grade está lendo.
 *
 * Com o passo em mãos a conta é direta: quantas casas são precisas para
 * dois valores distantes de um passo saírem diferentes.
 */
function rotuloEixo(v: number, escala: Escala, passo?: number): string {
  if (passo && passo > 0) return br(v, casasDoPasso(passo));
  if (escala === 'raiz') return br(v, v < 10 ? 1 : 0);
  const abs = Math.abs(v);
  if (abs >= 100) return br(v, 0);
  if (abs >= 10) return br(v, 1);
  if (abs >= 1) return br(v, 2);
  return br(v, 4);
}

/** A distância típica entre marcas, para o rótulo saber quantas casas usar. */
function passoDe(ms: number[]): number | undefined {
  if (ms.length < 2) return undefined;
  return Math.abs(ms[1] - ms[0]);
}

// ── Geometria ────────────────────────────────────────────────────────

export interface Ponto { x: number; y: number }

/**
 * Onde a reta que passa por `a` e `b` cruza a poligonal `curva`.
 *
 * A curva do ensaio é uma poligonal, não uma função: entre dois pontos
 * medidos não existe dado, existe interpolação. Cruzar segmento a
 * segmento é a leitura honesta disso — e devolve `null` quando a reta
 * simplesmente não encontra a curva dentro do medido, em vez de
 * extrapolar um valor que ninguém mediu.
 */
export function cruzamento(a: Ponto, b: Ponto, curva: Ponto[]): Ponto | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  // Distância com sinal do ponto à reta: onde ela troca de sinal, cruzou.
  const lado = (p: Ponto) => (p.x - a.x) * dy - (p.y - a.y) * dx;
  for (let i = 0; i + 1 < curva.length; i += 1) {
    const p = curva[i];
    const q = curva[i + 1];
    const lp = lado(p);
    const lq = lado(q);
    if (lp === 0) return p;
    if ((lp < 0) === (lq < 0)) continue;
    const t = lp / (lp - lq);
    return { x: p.x + t * (q.x - p.x), y: p.y + t * (q.y - p.y) };
  }
  return null;
}

/** O ponto da poligonal mais próximo de `alvo`, para o clique grudar nele. */
export function maisPerto(alvo: Ponto, pontos: Ponto[], conv: {
  x: Pick<Conversor, 'para'>; y: Pick<Conversor, 'para'>;
}): number {
  let melhor = 0;
  let dist = Infinity;
  pontos.forEach((p, i) => {
    // A distância é medida NA TELA, não no domínio: num eixo log, dois
    // pontos "próximos em kPa" podem estar a meia tela um do outro.
    const dx = conv.x.para(p.x) - conv.x.para(alvo.x);
    const dy = conv.y.para(p.y) - conv.y.para(alvo.y);
    const d = dx * dx + dy * dy;
    if (d < dist) { dist = d; melhor = i; }
  });
  return melhor;
}

// ── O plano ──────────────────────────────────────────────────────────

const M = { esq: 58, dir: 14, topo: 12, baixo: 34 };
const L = 760;
const A = 430;

/**
 * Um eixo com marcas e rótulos próprios — para quando a unidade que se LÊ
 * não é a unidade em que a curva está (√t em segundos no dado, √min no
 * eixo; t em segundos no dado, minutos no eixo). As marcas vêm em
 * unidades do DADO; o rótulo é o que se imprime ao lado.
 */
export interface EixoPersonalizado {
  marcas(lo: number, hi: number): number[];
  /** Linhas de grade sem rótulo (as de 2 e 5 numa década, por exemplo). */
  menores?(lo: number, hi: number): number[];
  /** `marcas` são todas as do eixo, para o rótulo decidir as casas pelo passo. */
  rotulo(v: number, marcas: number[]): string;
}

export interface PlanoProps {
  janela: Janela;
  eixoX?: EixoPersonalizado;
  escalaX?: Escala;
  escalaY?: Escala;
  tituloX: string;
  tituloY: string;
  /** Chamado quando o dedo se move com o botão apertado sobre o plano.
      `imantado` diz que `p` é uma leitura de verdade (de `ima`), não o
      lugar exato onde o cursor está. */
  aoArrastar?(p: Ponto, indiceAlca: number, imantado: boolean): void;
  /**
   * As leituras às quais as alças GRUDAM ao serem arrastadas. Com a
   * tecla Shift apertada a alça vai para onde o cursor estiver
   * (Humberto, 14/09/2026: «vincular a pontos reais do gráfico, ou não —
   * segurando o shift ao mover o ponto, libera a trava»).
   */
  ima?: Ponto[];
  /**
   * As alças que o ímã NÃO pega, pelo índice. O H₀ de Casagrande é uma
   * delas: ele mora ACIMA da curva (é a extrapolação do 0 % do primário),
   * e grudá-lo numa leitura seria grudá-lo no lugar errado.
   */
  alcasSemIma?: number[];
  /**
   * Quando informado, o plano passa a obedecer à roda (aproxima e afasta
   * em volta do cursor) e ao botão do meio (arrasta a janela com a
   * mãozinha), devolvendo a janela nova por aqui.
   */
  aoJanela?(j: Janela): void;
  /** Chamado num clique simples, com o ponto em unidades de ensaio. */
  aoClicar?(p: Ponto): void;
  children: (c: { x: Conversor; y: Conversor; px(p: Ponto): Ponto }) => React.ReactNode;
  /** Alças arrastáveis, em unidades de ensaio. */
  alcas?: Ponto[];
  desabilitado?: boolean;
}

/**
 * O plano cartesiano: eixos, grade e o que o chamador desenhar dentro.
 *
 * O SVG é de tamanho fixo (`viewBox`) e estica por CSS. É o que faz o
 * mesmo desenho servir ao monitor da mesa e ao tablet da bancada sem
 * recalcular nada — e o que deixa o gráfico entrar no PDF um dia, já que
 * lá também não há pixel, só `viewBox`.
 */
export function Plano({
  janela, escalaX = 'linear', escalaY = 'linear', tituloX, tituloY,
  aoArrastar, aoClicar, children, alcas = [], desabilitado, ima, alcasSemIma,
  aoJanela, eixoX,
}: PlanoProps) {
  const svg = useRef<SVGSVGElement>(null);
  const idRecorte = useId();
  const [arrastando, setArrastando] = useState<number | null>(null);
  /** O arrasto do PLANO (botão do meio): de onde partiu e a janela de então. */
  const movendo = useRef<{ u: number; v: number; janela: Janela } | null>(null);
  const [comAMao, setComAMao] = useState(false);

  const cx = useMemo(() => conversor(escalaX, janela.x0, janela.x1),
    [escalaX, janela.x0, janela.x1]);
  const cy = useMemo(() => conversor(escalaY, janela.y0, janela.y1),
    [escalaY, janela.y0, janela.y1]);

  const px = useCallback((p: Ponto): Ponto => ({
    x: M.esq + cx.para(p.x) * (L - M.esq - M.dir),
    // O y do SVG cresce para baixo; o do ensaio, para cima.
    y: A - M.baixo - cy.para(p.y) * (A - M.topo - M.baixo),
  }), [cx, cy]);

  /** A posição do cursor como fração da moldura (0…1 dentro dela; fora,
      além disso) — não depende da janela, só do pixel. */
  const normalizado = useCallback((e: { clientX: number; clientY: number }) => {
    const el = svg.current;
    if (!el) return null;
    const caixa = el.getBoundingClientRect();
    /*
     * O desenho NÃO ocupa a caixa inteira quando o CSS limita a altura
     * (`max-height` do gráfico): o `viewBox` mantém a proporção e centra
     * o desenho, com faixas vazias dos lados. Dividir pela largura da
     * caixa punha o clique alguns milímetros ao lado de onde caiu — numa
     * curva de adensamento com leitura a cada 15 s, a leitura vizinha
     * (onda 265, achado no teste 252.93). A escala é a do `meet`: a menor
     * das duas, com o desenho centrado.
     */
    const k = Math.min(caixa.width / L, caixa.height / A) || 1;
    const ox = caixa.left + (caixa.width - L * k) / 2;
    const oy = caixa.top + (caixa.height - A * k) / 2;
    const u = ((e.clientX - ox) / k - M.esq) / (L - M.esq - M.dir);
    const v = 1 - ((e.clientY - oy) / k - M.topo) / (A - M.topo - M.baixo);
    return { u, v };
  }, []);

  /** Do pixel do dedo de volta para as unidades do ensaio. */
  const doEvento = useCallback((e: React.PointerEvent): Ponto | null => {
    const n = normalizado(e);
    if (!n) return null;
    return { x: cx.de(Math.min(1, Math.max(0, n.u))), y: cy.de(Math.min(1, Math.max(0, n.v))) };
  }, [cx, cy, normalizado]);

  /*
   * A roda aproxima e afasta EM VOLTA DO CURSOR (Humberto, 14/09/2026):
   * o ponto da curva embaixo do ponteiro fica onde está e o resto se
   * abre ou se fecha ao redor dele — é o zoom de mapa, o que a mão
   * espera. Ouvinte nativo, não `onWheel` do React: o React registra a
   * roda como passiva e o `preventDefault` (que segura a página no lugar
   * enquanto se dá zoom no gráfico) não faz efeito por lá.
   */
  const janelaRef = useRef(janela);
  janelaRef.current = janela;
  useEffect(() => {
    const el = svg.current;
    if (!el || !aoJanela) return undefined;
    const aoRodar = (e: WheelEvent) => {
      const n = normalizado(e);
      if (!n) return;
      e.preventDefault();
      const f = e.deltaY > 0 ? 1.2 : 1 / 1.2;
      const u = Math.min(1, Math.max(0, n.u));
      const v = Math.min(1, Math.max(0, n.v));
      const soX = e.shiftKey;
      const soY = e.altKey;
      aoJanela(recortarJanela(janelaRef.current, escalaX, escalaY, {
        u0: soY ? 0 : u - u * f,
        u1: soY ? 1 : u + (1 - u) * f,
        v0: soX ? 0 : v - v * f,
        v1: soX ? 1 : v + (1 - v) * f,
      }));
    };
    el.addEventListener('wheel', aoRodar, { passive: false });
    return () => el.removeEventListener('wheel', aoRodar);
  }, [aoJanela, escalaX, escalaY, normalizado]);

  const marcasX = eixoX ? eixoX.marcas(janela.x0, janela.x1)
    : marcas(escalaX, janela.x0, janela.x1);
  const menoresX = eixoX?.menores?.(janela.x0, janela.x1) ?? [];
  const marcasY = marcas(escalaY, janela.y0, janela.y1, 5);
  /* No log as marcas são 1·2·5 por década e o «passo» não é constante —
     ali a regra antiga, por grandeza, é a certa. */
  const passoX = escalaX === 'log' ? undefined : passoDe(marcasX);
  const passoY = escalaY === 'log' ? undefined : passoDe(marcasY);

  return (
    <svg
      ref={svg}
      className={`plano${aoJanela ? ' com-mao' : ''}${comAMao ? ' movendo' : ''}`}
      viewBox={`0 0 ${L} ${A}`}
      role="img"
      aria-label={`${tituloY} contra ${tituloX}`}
      onAuxClick={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        /* Botão do meio: a mãozinha pega o plano inteiro e arrasta a
           janela. Vale mesmo com o traçado desabilitado — olhar não é
           traçar. O `preventDefault` segura a rolagem automática que o
           navegador liga no clique do meio. */
        if (e.button === 1 && aoJanela) {
          e.preventDefault();
          const n = normalizado(e);
          if (!n) return;
          movendo.current = { u: n.u, v: n.v, janela };
          setComAMao(true);
          (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
          return;
        }
        if (e.button !== 0) return;
        if (desabilitado) return;
        const p = doEvento(e);
        if (!p) return;
        if (alcas.length && aoArrastar) {
          const i = maisPerto(p, alcas, { x: cx, y: cy });
          const d = Math.hypot(cx.para(alcas[i].x) - cx.para(p.x),
            cy.para(alcas[i].y) - cy.para(p.y));
          // 6 % da janela é o raio do alvo: menos que isso e não se pega a
          // alça com o dedo no tablet; mais, e um clique na curva vira
          // arrasto sem querer.
          if (d < 0.06) {
            setArrastando(i);
            (e.target as Element).setPointerCapture?.(e.pointerId);
            return;
          }
        }
        aoClicar?.(p);
      }}
      onPointerMove={(e) => {
        const m = movendo.current;
        if (m && aoJanela) {
          const n = normalizado(e);
          if (!n) return;
          const du = n.u - m.u;
          const dv = n.v - m.v;
          aoJanela(recortarJanela(m.janela, escalaX, escalaY,
            { u0: -du, u1: 1 - du, v0: -dv, v1: 1 - dv }));
          return;
        }
        if (arrastando === null || !aoArrastar) return;
        const p = doEvento(e);
        if (!p) return;
        /* O ímã: sem Shift, a alça gruda na leitura mais próxima NA TELA;
           com Shift, vai para onde o cursor está. */
        if (ima?.length && !e.shiftKey && !alcasSemIma?.includes(arrastando)) {
          const i = maisPerto(p, ima, { x: cx, y: cy });
          aoArrastar(ima[i], arrastando, true);
          return;
        }
        aoArrastar(p, arrastando, false);
      }}
      onPointerUp={() => { setArrastando(null); movendo.current = null; setComAMao(false); }}
      onPointerCancel={() => { setArrastando(null); movendo.current = null; setComAMao(false); }}
    >
      {/* O papel do gráfico: um fundo próprio, mais claro que a página no
          escuro e mais branco no claro — a curva e as retas lidas sobre
          ele, não sobre o marrom do site (Humberto, 14/09/2026: «a cor
          de fundo do gráfico também não está legal»). */}
      <rect className="papel-plano" x={M.esq} y={M.topo}
        width={L - M.esq - M.dir} height={A - M.topo - M.baixo} />
      {/* grade */}
      {menoresX.map((v) => {
        const p = px({ x: v, y: janela.y0 });
        return <line key={`m${v}`} className="grade menor" x1={p.x} y1={M.topo} x2={p.x} y2={A - M.baixo} />;
      })}
      {marcasX.map((v) => {
        const p = px({ x: v, y: janela.y0 });
        return (
          <g key={`x${v}`}>
            <line className="grade" x1={p.x} y1={M.topo} x2={p.x} y2={A - M.baixo} />
            <text className="marca" x={p.x} y={A - M.baixo + 15} textAnchor="middle">
              {eixoX ? eixoX.rotulo(v, marcasX) : rotuloEixo(v, escalaX, passoX)}
            </text>
          </g>
        );
      })}
      {marcasY.map((v) => {
        const p = px({ x: janela.x0, y: v });
        return (
          <g key={`y${v}`}>
            <line className="grade" x1={M.esq} y1={p.y} x2={L - M.dir} y2={p.y} />
            <text className="marca" x={M.esq - 6} y={p.y + 4} textAnchor="end">
              {rotuloEixo(v, escalaY, passoY)}
            </text>
          </g>
        );
      })}

      {/* moldura */}
      <rect className="moldura" x={M.esq} y={M.topo}
        width={L - M.esq - M.dir} height={A - M.topo - M.baixo} />

      {/*
        * Tudo o que é DADO fica preso dentro da moldura.
        *
        * Sem o recorte, aproximar o gráfico desenhava a curva por cima
        * dos rótulos do eixo e para fora do quadro — e um ponto fora da
        * moldura não é decoração: é um ponto que parece estar num valor
        * que o eixo não mostra. Foi o que o teste 52.1 pegou assim que o
        * enquadramento automático passou a mostrar só o primário.
        *
        * As alças ficam FORA do recorte de propósito: elas são controle,
        * não dado, e uma alça arrastada para além da borda tem que
        * continuar visível para poder voltar.
        */}
      <clipPath id={`recorte-${idRecorte}`}>
        <rect x={M.esq} y={M.topo}
          width={L - M.esq - M.dir} height={A - M.topo - M.baixo} />
      </clipPath>
      <g clipPath={`url(#recorte-${idRecorte})`}>
        {children({ x: cx, y: cy, px })}
      </g>

      <text className="titulo-eixo" x={(L + M.esq) / 2} y={A - 4} textAnchor="middle">
        {tituloX}
      </text>
      <text className="titulo-eixo" x={12} y={(A - M.baixo + M.topo) / 2}
        textAnchor="middle" transform={`rotate(-90 12 ${(A - M.baixo + M.topo) / 2})`}>
        {tituloY}
      </text>
    </svg>
  );
}

// ── Peças de desenho ─────────────────────────────────────────────────

export function Serie({ pontos, px, marcar = true, classe, curva }: {
  pontos: Ponto[]; px(p: Ponto): Ponto; marcar?: boolean;
  /** Uma classe a mais no traço (a inicial tracejada da repetição, onda 323). */
  classe?: string;
  /** `data-curva` do traço («inicial», «atual»), por onde os testes leem. */
  curva?: string;
}) {
  if (!pontos.length) return null;
  const d = pontos.map((p, i) => {
    const q = px(p);
    return `${i ? 'L' : 'M'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`;
  }).join(' ');
  return (
    <>
      <path className={classe ? `serie ${classe}` : 'serie'} d={d} data-curva={curva} />
      {marcar && pontos.map((p, i) => {
        const q = px(p);
        return <circle key={i} className="ponto" cx={q.x} cy={q.y} r={3} />;
      })}
    </>
  );
}

/** Uma reta desenhada de ponta a ponta da moldura. */
export function Reta({ a, b, px, classe = 'reta' }: {
  a: Ponto; b: Ponto; px(p: Ponto): Ponto; classe?: string;
}) {
  const pa = px(a);
  const pb = px(b);
  const dx = pb.x - pa.x;
  const dy = pb.y - pa.y;
  if (!dx && !dy) return null;
  // Estica até bater na moldura, para a reta ser lida como reta e não
  // como um segmento entre duas alças.
  const ts: number[] = [];
  if (dx) {
    ts.push((M.esq - pa.x) / dx, (L - M.dir - pa.x) / dx);
  }
  if (dy) {
    ts.push((M.topo - pa.y) / dy, (A - M.baixo - pa.y) / dy);
  }
  const dentro = (t: number) => {
    const x = pa.x + t * dx;
    const y = pa.y + t * dy;
    return x >= M.esq - 0.5 && x <= L - M.dir + 0.5
      && y >= M.topo - 0.5 && y <= A - M.baixo + 0.5;
  };
  const bons = ts.filter(dentro).sort((m, n) => m - n);
  if (bons.length < 2) return null;
  const t0 = bons[0];
  const t1 = bons[bons.length - 1];
  return (
    <line className={classe}
      x1={pa.x + t0 * dx} y1={pa.y + t0 * dy}
      x2={pa.x + t1 * dx} y2={pa.y + t1 * dy} />
  );
}

/**
 * Um SEGMENTO — de a até b, e nada além.
 *
 * `Reta` estica até a moldura de propósito (é como uma reta de ajuste se
 * lê). A construção de Pacheco Silva é o contrário: cada traço vai de um
 * ponto a outro e para ali — a horizontal de e₀ até a reta virgem, a
 * vertical até a curva, a horizontal de volta à reta, a vertical até o
 * eixo. Esticadas, as quatro viram uma grade e a construção some
 * (Humberto, 04/09/2026).
 */
export function Segmento({ a, b, px, classe = 'reta' }: {
  a: Ponto; b: Ponto; px(p: Ponto): Ponto; classe?: string;
}) {
  const pa = px(a);
  const pb = px(b);
  if (![pa.x, pa.y, pb.x, pb.y].every(Number.isFinite)) return null;
  return <line className={classe} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />;
}

/** Um ponto de construção — pequeno, com a letra ao lado, sem alça. */
export function Marco({ p, px, rotulo }: { p: Ponto; px(q: Ponto): Ponto; rotulo?: string }) {
  const q = px(p);
  if (!Number.isFinite(q.x) || !Number.isFinite(q.y)) return null;
  return (
    <g className="marco">
      <circle cx={q.x} cy={q.y} r={3.5} />
      {rotulo && <text x={q.x + 7} y={q.y - 6}>{rotulo}</text>}
    </g>
  );
}

export function Alca({ p, px, rotulo, imantada }: {
  p: Ponto; px(q: Ponto): Ponto; rotulo?: string;
  /** A alça está sobre uma leitura de verdade (o ímã pegou). */
  imantada?: boolean;
}) {
  const q = px(p);
  return (
    <g className={imantada ? 'alca imantada' : 'alca'}>
      <circle cx={q.x} cy={q.y} r={9} className="alca-alvo" />
      <circle cx={q.x} cy={q.y} r={5} className="alca-miolo" />
      {rotulo && <text x={q.x + 12} y={q.y - 8}>{rotulo}</text>}
    </g>
  );
}

export function Achado({ p, px, rotulo }: { p: Ponto; px(q: Ponto): Ponto; rotulo: string }) {
  const q = px(p);
  return (
    <g className="achado">
      <circle cx={q.x} cy={q.y} r={6} />
      <text x={q.x + 10} y={q.y + 4}>{rotulo}</text>
    </g>
  );
}

// ── Zoom ─────────────────────────────────────────────────────────────

/**
 * Enquadra os dados e depois obedece ao usuário.
 *
 * "Os zooms vertical e horizontal dos gráficos deverão ser ajustáveis
 * para o cálculo dos Cv, mas inicialmente ele se autoajusta para melhor
 * enquadrar" (Testesolo, 23/08/2026).
 *
 * O enquadramento automático é recalculado sempre que os DADOS mudam, e
 * nunca depois que o usuário mexeu: um gráfico que volta ao automático
 * sozinho é um gráfico que desfaz o trabalho de quem estava traçando.
 */
export function useJanela(
  pontos: Ponto[],
  folga = 0.06,
  escalas: { x?: Escala; y?: Escala } = {},
) {
  const ex = escalas.x ?? 'linear';
  const ey = escalas.y ?? 'linear';

  /*
   * A folga é dada NA ESCALA DO EIXO, e não em unidades do ensaio.
   *
   * Defeito visto na primeira renderização, 23/08/2026: a curva de
   * compressibilidade saiu desenhada FORA da moldura, encostada à
   * esquerda, e o eixo mostrou rótulos sem sentido. A causa era uma
   * subtração inocente — a folga de 8 % do intervalo (640 − 2 = 638, logo
   * 51 kPa) tirada do mínimo dava **−49 kPa**, e `log10` de número
   * negativo é `NaN`. A partir daí todo o desenho é NaN, e NaN não
   * reclama: só não aparece onde deveria.
   *
   * Num eixo log a folga é multiplicativa; num eixo de raiz, o zero é
   * chão e não se ultrapassa — é justamente em √t = 0 que se lê o d₀ de
   * Taylor.
   */
  const auto = useMemo<Janela>(() => {
    if (!pontos.length) return { x0: 0, x1: 1, y0: 0, y1: 1 };
    const folgar = (lo: number, hi: number, escala: Escala): [number, number] => {
      if (escala === 'log') {
        const a = Math.max(lo, 1e-9);
        const b = Math.max(hi, a * 10);
        const f = (Math.log10(b) - Math.log10(a)) * folga || 0.05;
        return [10 ** (Math.log10(a) - f), 10 ** (Math.log10(b) + f)];
      }
      if (escala === 'raiz') {
        const f = (Math.sqrt(Math.max(hi, 0)) - Math.sqrt(Math.max(lo, 0))) * folga;
        const a = Math.max(0, Math.sqrt(Math.max(lo, 0)) - f);
        const b = Math.sqrt(Math.max(hi, 0)) + (f || 1);
        return [a * a, b * b];
      }
      const f = (hi - lo) * folga || Math.abs(lo * folga) || 1;
      return [lo - f, hi + f];
    };
    const xs = pontos.map((p) => p.x);
    const ys = pontos.map((p) => p.y);
    const [x0, x1] = folgar(Math.min(...xs), Math.max(...xs), ex);
    const [y0, y1] = folgar(Math.min(...ys), Math.max(...ys), ey);
    return { x0, x1, y0, y1 };
  }, [pontos, folga, ex, ey]);

  const [manual, setManual] = useState<Janela | null>(null);
  const janela = manual ?? auto;

  /** Aproxima ou afasta em um eixo, mantendo o centro. */
  const ampliar = (eixo: 'x' | 'y', fator: number) => {
    const j = janela;
    const a = eixo === 'x' ? j.x0 : j.y0;
    const b = eixo === 'x' ? j.x1 : j.y1;
    const meio = (a + b) / 2;
    const meia = ((b - a) / 2) * fator;
    const escala = eixo === 'x' ? ex : ey;
    /* Piso: nem log nem raiz existem em número negativo, e um zoom que
       leva o eixo para lá apaga o gráfico inteiro em NaN. */
    const piso = escala === 'log' ? Math.max(1e-9, a * 0.001)
      : escala === 'raiz' ? 0 : -Infinity;
    const novoA = Math.max(piso, meio - meia);
    setManual(eixo === 'x'
      ? { ...j, x0: novoA, x1: meio + meia }
      : { ...j, y0: novoA, y1: meio + meia });
  };

  const deslocar = (eixo: 'x' | 'y', passos: number) => {
    const j = janela;
    const a = eixo === 'x' ? j.x0 : j.y0;
    const b = eixo === 'x' ? j.x1 : j.y1;
    const d = (b - a) * 0.15 * passos;
    const escala = eixo === 'x' ? ex : ey;
    const piso = escala === 'log' ? Math.max(1e-9, a * 0.001)
      : escala === 'raiz' ? 0 : -Infinity;
    setManual(eixo === 'x'
      ? { ...j, x0: Math.max(piso, j.x0 + d), x1: j.x1 + d }
      : { ...j, y0: Math.max(piso, j.y0 + d), y1: j.y1 + d });
  };

  return {
    janela, auto, manual, ampliar, deslocar,
    /** A janela que a roda ou a mãozinha pediram, inteira. */
    definir: (j: Janela) => setManual(j),
    reenquadrar: () => setManual(null),
  };
}

export function Zoom({ z, desabilitado }: {
  z: ReturnType<typeof useJanela>; desabilitado?: boolean;
}) {
  const b = (rot: string, titulo: string, fn: () => void) => (
    <button type="button" className="discreto" title={titulo}
      disabled={desabilitado} onClick={fn}>{rot}</button>
  );
  return (
    <div className="zoom">
      <span className="rotulo-zoom">horizontal</span>
      {b('−', 'afastar na horizontal', () => z.ampliar('x', 1.4))}
      {b('+', 'aproximar na horizontal', () => z.ampliar('x', 1 / 1.4))}
      {b('◂', 'deslocar para a esquerda', () => z.deslocar('x', -1))}
      {b('▸', 'deslocar para a direita', () => z.deslocar('x', 1))}
      <span className="rotulo-zoom">vertical</span>
      {b('−', 'afastar na vertical', () => z.ampliar('y', 1.4))}
      {b('+', 'aproximar na vertical', () => z.ampliar('y', 1 / 1.4))}
      {b('▾', 'deslocar para baixo', () => z.deslocar('y', -1))}
      {b('▴', 'deslocar para cima', () => z.deslocar('y', 1))}
      <button type="button" className="discreto" onClick={z.reenquadrar}
        title="voltar ao enquadramento automático">
        reenquadrar
      </button>
    </div>
  );
}
